<template>
  <div class="panel-layout">
    <!-- Mobile backdrop -->
    <div
      v-if="mobileOpen"
      class="panel-backdrop"
      @click="mobileOpen = false"
    ></div>

    <!-- Floating sidebar -->
    <aside
      class="panel-sidebar"
      :class="{ collapsed: collapsed, 'mobile-open': mobileOpen }"
    >
      <div class="sidebar-header">
        <router-link :to="homeLink" class="sidebar-logo" :title="homeLinkTitle">
          <img :src="logoUrl" alt="Kame Seeds" />
        </router-link>
        <button class="sidebar-toggle d-lg-none" @click="mobileOpen = false" title="Cerrar">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <nav class="sidebar-nav">
        <template v-for="(group, gi) in navGroups" :key="gi">
          <div v-if="group.title" class="nav-section-title">{{ group.title }}</div>
          <router-link
            v-for="item in group.items"
            :key="item.to"
            :to="item.to"
            class="nav-item"
            :class="{ active: isActive(item) }"
            @click="mobileOpen = false"
          >
            <i :class="item.icon"></i>
            <span class="nav-label">{{ item.label }}</span>
            <span
              v-if="item.badge && item.badge.value > 0"
              class="badge"
              :class="item.badge.variant"
            >{{ item.badge.value }}</span>
          </router-link>
        </template>
      </nav>

      <div class="sidebar-footer">
        <div class="user-chip" :title="username">
          <span class="user-avatar">{{ initials }}</span>
          <span class="user-meta">
            <span class="user-name">{{ username }}</span>
            <span class="user-role">{{ userRoleLabel }}</span>
          </span>
        </div>
        <button @click="handleLogout" class="logout-btn">
          <i class="fas fa-sign-out-alt"></i>
          <span class="nav-label">Cerrar sesión</span>
        </button>
      </div>
    </aside>

    <!-- Main content -->
    <div class="panel-content" :class="{ expanded: collapsed }">
      <header class="panel-topbar">
        <button class="topbar-burger d-lg-none" @click="mobileOpen = true" title="Menú">
          <i class="fas fa-bars"></i>
        </button>
        <div class="topbar-title">
          <h1>{{ pageTitle }}</h1>
        </div>
        <div class="topbar-actions">
          <a
            v-if="!isIngresoPanel"
            href="/"
            target="_blank"
            rel="noopener"
            class="topbar-btn topbar-btn-store"
          >
            <i class="fas fa-store"></i>
            <span class="topbar-btn-label">Ver tienda</span>
          </a>
          <a
            v-else
            href="/"
            target="_blank"
            rel="noopener"
            class="topbar-btn topbar-btn-store topbar-btn-store-muted"
          >
            <i class="fas fa-external-link-alt"></i>
            <span class="topbar-btn-label">Sitio público</span>
          </a>
          <button
            v-if="isIngresoPanel && ingresoCartCount > 0"
            type="button"
            class="topbar-btn topbar-btn-cart"
            @click="openIngresoCart"
          >
            <i class="fas fa-shopping-cart"></i>
            <span class="topbar-btn-label">Carrito</span>
            <span class="topbar-cart-badge">{{ ingresoCartCount }}</span>
          </button>
        </div>
      </header>

      <main class="panel-main">
        <div class="panel-main-view">
          <router-view v-slot="{ Component, route }">
            <transition name="fade-slide">
              <component :is="Component" :key="route.path" />
            </transition>
          </router-view>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { logout, verifyPermissions } from '@/services/auth'
import { canAccessWholesale as checkWholesale } from '@/utils/b2bAccess'
import { isIngresoHost } from '@/utils/host'
import { useIngresoCart } from '@/composables/useIngresoCart'
import apiClient from '@/services/apiClient'
import logoUrl from '@/assets/images/LOGO.svg'

const route = useRoute()
const router = useRouter()
const { cartCount: ingresoCartCount, openCart: openIngresoCart } = useIngresoCart()

const collapsed = ref(localStorage.getItem('panelSidebarCollapsed') === 'true')
const mobileOpen = ref(false)
const isAdmin = ref(localStorage.getItem('isAdmin') === 'true')
const pendingOrders = ref(0)
const pendingApprovals = ref(0)
const canAccessWholesale = ref(checkWholesale())
const canShop = computed(
  () => canAccessWholesale.value || localStorage.getItem('isApproved') === 'true'
)

const isIngresoPanel = computed(
  () =>
    route.meta.panelVariant === 'ingreso' ||
    route.path.startsWith('/ingreso') ||
    isIngresoHost()
)

const homeLink = computed(() => (isIngresoPanel.value ? '/panel/pedido-rapido' : '/'))
const homeLinkTitle = computed(() =>
  isIngresoPanel.value ? 'Catálogo' : 'Ir a la tienda'
)

const userRoleLabel = computed(() => {
  if (isAdmin.value) return 'Administrador'
  if (isIngresoPanel.value) return 'Cliente autorizado'
  return 'Cliente'
})

const username = computed(() => localStorage.getItem('username') || 'Mi cuenta')
const initials = computed(() => {
  const name = username.value.trim()
  const parts = name.split(/[\s@.]+/).filter(Boolean)
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'KS'
})

watch(collapsed, (v) => localStorage.setItem('panelSidebarCollapsed', String(v)))

const navGroups = computed(() => {
  if (isIngresoPanel.value) {
    return [
      {
        title: 'TIENDA',
        items: [
          { to: '/panel/pedido-rapido', label: 'Catálogo', icon: 'fas fa-th-large' },
          { to: '/panel/mis-pedidos', label: 'Mis pedidos', icon: 'fas fa-receipt' },
        ],
      },
      {
        title: 'CUENTA',
        items: [
          { to: '/ingreso/cuenta', label: 'Configuración', icon: 'fas fa-user-cog' },
        ],
      },
    ]
  }

  if (isAdmin.value) {
    return [
      {
        title: 'CATÁLOGO',
        items: [
          { to: '/panel/productos', label: 'Productos', icon: 'fas fa-seedling' },
          { to: '/panel/envios', label: 'Envíos', icon: 'fas fa-truck' },
          { to: '/panel/banners', label: 'Banners', icon: 'fas fa-images' },
          { to: '/panel/galeria-inicio', label: 'Galería inicio', icon: 'fas fa-photo-video' },
          { to: '/panel/trofeos', label: 'Trofeos', icon: 'fas fa-trophy' },
          { to: '/panel/blog', label: 'Blog', icon: 'fas fa-newspaper' },
        ],
      },
      {
        title: 'VENTAS',
        items: [
          { to: '/panel/estadisticas', label: 'Estadísticas', icon: 'fas fa-chart-line' },
          { to: '/panel/pedidos', label: 'Pedidos', icon: 'fas fa-shopping-bag', badge: { value: pendingOrders.value, variant: 'badge-danger' } },
          { to: '/panel/promociones', label: 'Cupones y ofertas', icon: 'fas fa-tags' },
        ],
      },
      {
        title: 'CLIENTES',
        items: [
          { to: '/panel/usuarios', label: 'Usuarios', icon: 'fas fa-users', badge: { value: pendingApprovals.value, variant: 'badge-warning' } },
          { to: '/panel/invitaciones', label: 'Invitaciones', icon: 'fas fa-key' },
          { to: '/panel/roles', label: 'Roles', icon: 'fas fa-shield-alt' },
          { to: '/panel/configuracion', label: 'Configuración', icon: 'fas fa-cog' },
        ],
      },
      {
        title: 'CUENTA',
        items: [
          { to: '/panel/cuenta', label: 'Mi cuenta', icon: 'fas fa-user-circle' },
        ],
      },
    ]
  }

  return [
    {
      title: 'MI CUENTA',
      items: [
        { to: '/panel/cuenta', label: 'Configuración', icon: 'fas fa-user-circle' },
        { to: '/panel/mis-pedidos', label: 'Mis pedidos', icon: 'fas fa-shopping-bag' },
      ],
    },
    ...(canShop.value ? [{
      title: 'COMPRAS',
      items: [
        { to: '/panel/pedido-rapido', label: 'Catálogo', icon: 'fas fa-th-large' },
      ],
    }] : []),
  ]
})

const isActive = (item) => {
  const base = item.to
  if (route.path === base) return true
  // Parent-match for nested detail/form routes (e.g. /panel/productos/nuevo)
  return route.path.startsWith(base + '/')
}

const pageTitles = {
  PanelStatistics: 'Estadísticas',
  AdminProductList: 'Productos',
  AdminOrderList: 'Pedidos',
  AdminOrderDetails: 'Detalle de pedido',
  ProductForm: 'Nuevo producto',
  ProductEdit: 'Editar producto',
  UserList: 'Usuarios',
  UserDetail: 'Detalle de usuario',
  PanelBanners: 'Banners',
  HomeGalleryManager: 'Galería del inicio',
  TrophiesManager: 'Trofeos',
  BlogList: 'Blog',
  BlogNew: 'Nuevo artículo',
  BlogEdit: 'Editar artículo',
  PanelMarketing: 'Cupones y ofertas',
  PanelInvitations: 'Invitaciones',
  PanelRoles: 'Roles de cliente',
  PanelShippingOrders: 'Envíos',
  PanelShippingSettings: 'Configuración de envíos',
  PanelStoreSettings: 'Configuración comercial',
  PanelAccount: 'Configuración de cuenta',
  PanelOrders: 'Mis pedidos',
  PanelOrderDetail: 'Detalle del pedido',
  IngresoOrderDetail: 'Detalle del pedido',
  DistributorShop: 'Catálogo',
  IngresoShop: 'Catálogo',
  IngresoProductDetail: 'Detalle de producto',
  IngresoOrders: 'Mis pedidos',
  IngresoAccount: 'Mi cuenta',
}
const pageTitle = computed(() => pageTitles[route.name] || 'Panel')

const handleLogout = () => {
  logout()
  router.push('/login')
}

onMounted(async () => {
  try {
    const permissions = await verifyPermissions()
    isAdmin.value = !!(permissions.is_staff || permissions.is_superuser)
    canAccessWholesale.value = checkWholesale()
  } catch {
    // keep localStorage fallback
  }

  if (isAdmin.value) {
    try {
      const [ordersRes, usersRes] = await Promise.all([
        apiClient.get('/orders/stats/').catch(() => null),
        apiClient.get('/auth/access-requests/?status=pending&page_size=1').catch(() => null),
      ])
      if (ordersRes?.data) {
        pendingOrders.value = (ordersRes.data.pending_orders || 0) + (ordersRes.data.payment_pending_orders || 0)
      }
      if (usersRes?.data) {
        pendingApprovals.value = usersRes.data.count || 0
      }
    } catch {
      // badges are cosmetic
    }
  }
})
</script>

<style scoped>
.panel-layout {
  min-height: 100vh;
  background-color: var(--black-color);
  background-image: url('@/assets/images/fondos/fondo-catalogo.svg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  color: var(--white-color);
  font-family: 'Poppins', sans-serif;
  letter-spacing: normal;
}

/* ── Floating sidebar ── */
.panel-sidebar {
  position: fixed;
  top: 16px;
  left: 16px;
  bottom: 16px;
  width: 248px;
  display: flex;
  flex-direction: column;
  background: var(--grey-color);
  border: 1px solid var(--black-color-2);
  border-radius: 18px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.45);
  z-index: 1050;
  transition: width 0.25s ease, transform 0.25s ease;
  overflow: hidden;
}
.panel-sidebar.collapsed { width: 76px; }

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1.1rem;
  border-bottom: 1px solid var(--black-color-2);
  min-height: 64px;
}
.sidebar-logo img { height: 84px; transition: opacity 0.2s; display: flex; align-items: center; justify-content: center; }
.panel-sidebar.collapsed .sidebar-logo { width: 0; opacity: 0; overflow: hidden; }
.sidebar-toggle {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}
.sidebar-toggle:hover { background: var(--black-color-2); color: var(--white-color); }

/* Nav */
.sidebar-nav {
  flex: 1;
  padding: 0.75rem 0.6rem;
  overflow-y: auto;
  overflow-x: hidden;
}
.sidebar-nav::-webkit-scrollbar { width: 6px; }
.sidebar-nav::-webkit-scrollbar-thumb { background: var(--black-color-2); border-radius: 3px; }

.nav-section-title {
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  color: rgba(255, 255, 255, 0.45);
  padding: 0.9rem 0.85rem 0.35rem;
  white-space: nowrap;
}
.panel-sidebar.collapsed .nav-section-title { opacity: 0; height: 0; padding: 0; overflow: hidden; }

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.62rem 0.85rem;
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 10px;
  margin: 2px 0;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  position: relative;
}
.nav-item i { width: 20px; text-align: center; font-size: 1rem; flex-shrink: 0; }
.nav-label { transition: opacity 0.2s; }
.panel-sidebar.collapsed .nav-label { opacity: 0; width: 0; overflow: hidden; }
.panel-sidebar.collapsed .nav-item { justify-content: center; }
.nav-item:hover { background: var(--black-color-2); color: var(--white-color); }
.nav-item.active {
  background: var(--primary-color);
  color: var(--black-color);
}
.nav-item.active::before {
  content: '';
  position: absolute;
  left: -0.6rem;
  top: 18%;
  bottom: 18%;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--primary-color);
}

.badge {
  margin-left: auto;
  color: var(--white-color);
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 0.68rem;
  font-weight: 700;
  flex-shrink: 0;
  min-width: 1.25rem;
  text-align: center;
  line-height: 1.4;
}
.panel-sidebar.collapsed .badge {
  position: absolute;
  top: 2px;
  right: 4px;
  margin: 0;
  padding: 1px 5px;
  font-size: 0.58rem;
  min-width: 1rem;
  border: 2px solid var(--grey-color);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
}
.nav-item.active .badge {
  background: var(--black-color) !important;
  color: var(--white-color);
  border: 1px solid rgba(0, 0, 0, 0.2);
}
.badge-danger { background: #e5484d; }
.badge-warning { background: var(--orange-color); color: var(--black-color); }
.nav-item.active .badge-warning {
  background: var(--black-color) !important;
  color: var(--white-color) !important;
}

/* Footer */
.sidebar-footer { border-top: 1px solid var(--black-color-2); padding: 0.75rem; }
.user-chip {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.4rem 0.5rem;
  border-radius: 10px;
  margin-bottom: 0.4rem;
  overflow: hidden;
}
.user-avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--primary-color);
  color: var(--black-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
}
.user-meta { display: flex; flex-direction: column; line-height: 1.15; overflow: hidden; }
.user-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--white-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-role { font-size: 0.72rem; color: rgba(255, 255, 255, 0.5); }
.panel-sidebar.collapsed .user-meta { display: none; }

.logout-btn {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  padding: 0.6rem 0.85rem;
  background: none;
  border: none;
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 500;
  transition: background 0.15s, color 0.15s;
}
.logout-btn i { width: 20px; text-align: center; }
.logout-btn:hover { background: rgba(255, 59, 48, 0.15); color: #ff6b6b; }
.panel-sidebar.collapsed .logout-btn { justify-content: center; }

/* ── Content ── */
.panel-content {
  margin-left: 280px;
  padding: 16px 16px 16px 0;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  transition: margin-left 0.25s ease;
}
.panel-content.expanded { margin-left: 108px; }

.panel-topbar {
  position: sticky;
  top: 16px;
  z-index: 40;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 1rem;
  padding: 0 1.25rem;
  min-height: 64px;
  background: var(--grey-color);
  border: 1px solid var(--black-color-2);
  border-radius: 18px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  margin-bottom: 16px;
}
.topbar-burger {
  background: var(--black-color-2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--white-color);
  width: 36px;
  height: 36px;
  border-radius: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.topbar-title { flex: 1; min-width: 0; }
.topbar-title h1 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: var(--white-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}
.topbar-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  margin-left: auto;
}

.topbar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  height: 36px;
  padding: 0 0.85rem;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  flex-shrink: 0;
  position: static;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.topbar-btn i {
  font-size: 0.9rem;
  line-height: 1;
  flex-shrink: 0;
}

.topbar-btn-label {
  display: none;
}

@media (min-width: 576px) {
  .topbar-btn-label {
    display: inline;
  }
}

.topbar-btn-store {
  border: 1px solid var(--black-color-2);
  background: var(--black-color-2);
  color: var(--white-color);
}

.topbar-btn-store:hover {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: var(--black-color);
}

.topbar-btn-store-muted {
  background: transparent;
}

.topbar-btn-store-muted:hover {
  background: var(--black-color-2);
  border-color: rgba(255, 255, 255, 0.15);
  color: var(--white-color);
}

.topbar-btn-cart {
  border: 1px solid rgba(255, 236, 0, 0.35);
  background: rgba(255, 236, 0, 0.1);
  color: var(--primary-color);
  font-weight: 600;
}

.topbar-btn-cart:hover {
  background: var(--primary-color);
  color: var(--black-color);
}

.topbar-cart-badge {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--primary-color);
  color: var(--black-color);
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.topbar-btn-cart:hover .topbar-cart-badge {
  background: var(--black-color);
  color: var(--primary-color);
}

.panel-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 1.25rem 1.5rem;
  background: var(--grey-color);
  border: 1px solid var(--black-color-2);
  border-radius: 18px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}

.panel-main-view {
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Page transition — crossfade sin hueco vacío entre rutas */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.12s ease;
}
.fade-slide-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  pointer-events: none;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
}

/* Mobile backdrop */
.panel-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1040;
}

/* Responsive */
@media (max-width: 991.98px) {
  .panel-sidebar {
    transform: translateX(calc(-100% - 24px));
    width: 268px;
  }
  .panel-sidebar.mobile-open { transform: translateX(0); }
  .panel-content,
  .panel-content.expanded {
    margin-left: 0;
    padding: 12px;
  }
  .panel-topbar { top: 12px; }
  .panel-main { padding: 1rem; }
}
</style>
